import { z } from 'zod';
import { intish, isoDateTime, laravelPaginator, looseBoolean, wireDecimal } from './common';
import type { OrganizationType } from './auth';

/**
 * Order endpoints (V1-05) — wire schemas + normalized internal model.
 * Grounded in docs/05a-api-baseline.md §10.2 and
 * pos-api/app/Http/Controllers/Api/POS/OrderController.php:
 *
 *   POST /orders                 201 { success, order, message }   (V1-04 owns create)
 *   GET  /orders                 raw Laravel paginator (rows carry `terminal`, never `area`)
 *   GET  /orders/new             { success, data: { pending, inprogress } } (no cancelled/delivered)
 *   GET  /orders/{id}            { success, data } · 404 { success:false, error:{code,message} }
 *   PUT  /orders/{id}/status     200 { success, message } — NO order body; consumers re-fetch
 *
 * STATUS TOLERANCE: the API accepts the complete restaurant and ecommerce
 * status set, but `status` remains a PLAIN STRING at the wire layer so a future
 * or externally written status stays visible instead of dropping an order
 * (03-pos-shell.md "Status and stage model").
 */

// ---------------------------------------------------------------------------
// Wire constants
// ---------------------------------------------------------------------------

/** The seven restaurant workflow statuses, retained as a distinct presentation set. */
export const POS_ORDER_STATUSES = [
  'pending',
  'confirmed',
  'preparing',
  'ready_for_pickup',
  'out_for_delivery',
  'delivered',
  'cancelled',
] as const;
export type PosOrderStatus = (typeof POS_ORDER_STATUSES)[number];

/**
 * The retail fulfilment statuses (salesgroundai `App\Enums\OrderStatus`), which
 * a website order can arrive holding and which a shop's own board now writes.
 */
export const ECOMMERCE_EXTRA_STATUSES = ['ready_for_dispatch', 'shipped', 'refunded'] as const;

/**
 * Values `PUT /orders/{id}/status` accepts.
 *
 * This was the seven POS statuses alone, because pos-api's enum was the
 * restaurant set and its validation is `Rule::in(OrderStatus::cases())`. The
 * consequence was structural rather than cosmetic: the retail board's own
 * transitions — Packed (`ready_for_dispatch`) and Mark shipped (`shipped`) —
 * answered 422, so a shop could accept an order and then never advance it. The
 * Shipped column could not be reached by any route.
 *
 * pos-api's enum now mirrors the core's ten
 * (pos-api/app/Enums/OrderStatus.php), so the retail transitions are executable
 * and `transitionsFor` stops marking them blocked.
 *
 * Kept as its own constant rather than collapsed into one list: "what the wire
 * can CONTAIN" and "what this endpoint will ACCEPT" are different questions
 * that have already been different once, and naming them apart is what let the
 * queue offer a blocked transition honestly instead of failing at the counter.
 */
export const POS_API_PUT_STATUSES: readonly string[] = [
  ...POS_ORDER_STATUSES,
  ...ECOMMERCE_EXTRA_STATUSES,
];

/** Human label for any wire status, including unknown ones ("some_status" → "Some status"). */
/**
 * What a SHOP calls each wire status.
 *
 * The wire is one set of values for every tenant — the server keeps a single
 * enum and the website writes to the same rows — so a retail order can and does
 * hold statuses named for a kitchen. Prettifying the raw value therefore put
 * "Preparing" on a shop's timeline and its badge: a word from a trade that
 * tenant does not work in, describing a parcel nobody is cooking.
 *
 * Only the statuses whose restaurant wording is actually wrong for a shop are
 * listed. `confirmed`, `shipped`, `delivered`, `cancelled` and `refunded` mean
 * the same thing in both, and repeating them here would be two places to change
 * one word.
 */
const RETAIL_STATUS_WORDS: Record<string, string> = {
  pending: 'Awaiting acceptance',
  confirmed: 'Accepted',
  // The stage a shop calls Packing — the board already labels the column that
  // way (queue/stages.ts), and a timeline that disagrees with the column the
  // card sits in is the same screen naming one state twice.
  preparing: 'Packing',
  ready_for_dispatch: 'Packed',
  // Restaurant fulfilment words that a website order can still carry.
  ready_for_pickup: 'Ready for collection',
  out_for_delivery: 'Out for delivery',
};

/**
 * Human label for any wire status, including unknown ones
 * ("some_status" → "Some status").
 *
 * `organizationType` is optional and defaults to the neutral wording this
 * function has always returned, so a caller that has not been given the type
 * cannot silently change what it prints.
 */
export function orderStatusLabel(
  status: string,
  organizationType?: OrganizationType | null,
): string {
  if (organizationType === 'ecommerce') {
    const retail = RETAIL_STATUS_WORDS[status];
    if (retail) return retail;
  }
  const words = status.replace(/[_-]+/g, ' ').trim();
  if (!words) return 'Unknown';
  return words.charAt(0).toUpperCase() + words.slice(1);
}

// ---------------------------------------------------------------------------
// Wire schemas (tolerant: loose objects, plain-string enums-of-record)
// ---------------------------------------------------------------------------

/** `{}` serializes as `[]` when empty (PHP array quirk) — both accepted. */
const wireAttributes = z.union([z.record(z.string(), z.unknown()), z.array(z.unknown())]);

const wireOrderProductSchema = z.looseObject({
  id: z.number(),
  /** Root catalog product used by the admin product link for variant lines. */
  catalog_id: z.number().nullish(),
  name: z.string().nullish(),
  sku: z.string().nullish(),
  /** Gallery paths are present on the eager-loaded product relation. */
  images: z.array(z.string()).nullish(),
  /** Immutable purchase-time variant identity when the order carries a snapshot. */
  variant_name: z.string().nullish(),
  variant_label: z.string().nullish(),
});

export const wireOrderItemSchema = z.looseObject({
  id: z.number(),
  order_id: z.number().nullish(),
  product_id: z.number(),
  parent_order_item_id: z.number().nullish(),
  quantity: intish,
  unit_price: wireDecimal,
  total_price: wireDecimal,
  attributes: wireAttributes.nullish(),
  meta: z.union([z.record(z.string(), z.unknown()), z.array(z.unknown())]).nullish(),
  product: wireOrderProductSchema.nullish(),
});
export type WireOrderItem = z.infer<typeof wireOrderItemSchema>;

export const wireOrderStatusLogSchema = z.looseObject({
  id: z.number(),
  order_id: z.number().nullish(),
  status: z.string(),
  notes: z.string().nullish(),
  created_at: isoDateTime,
});
export type WireOrderStatusLog = z.infer<typeof wireOrderStatusLogSchema>;

const wireOrderAreaSchema = z.looseObject({
  id: z.number(),
  name: z.string().nullish(),
  delivery_charges: wireDecimal.nullish(),
});

const wireOrderTerminalSchema = z.looseObject({
  id: z.number(),
  name: z.string().nullish(),
});

/** Eager-loaded on the active board and on detail so a tab card can be named. */
const wireOrderTableSchema = z.looseObject({
  id: z.number(),
  label: z.string().nullish(),
});

const wireOrderPromotionSchema = z.looseObject({
  id: z.number(),
  promotion_id: z.number().nullish(),
  title: z.string(),
  code: z.string().nullish(),
  method: z.string(),
  promotion_type: z.string(),
  currency: z.string(),
  merchandise_savings: wireDecimal,
  shipping_savings: wireDecimal,
  allocations: z
    .array(
      z.looseObject({
        order_item_id: z.number(),
        amount: wireDecimal,
      }),
    )
    .default([]),
  state: z.string(),
});

const wireOrderPaymentReceiptSchema = z.looseObject({
  available: looseBoolean,
  original_name: z.string().nullish(),
  mime_type: z.string().nullish(),
  size: z.number().nullish(),
  uploaded_at: isoDateTime.nullish(),
});

const wireOrderPaymentSchema = z.looseObject({
  checkout_id: z.string().nullish(),
  method: z.string(),
  name: z.string(),
  type: z.string(),
  status: z.string(),
  reference: z.string().nullish(),
  receipt: wireOrderPaymentReceiptSchema.nullish(),
});

const wireOrderStatusSummarySchema = z.object({
  order: z.string(),
  payment: z.string(),
  fulfillment: z.string(),
  return: z.string(),
  paid_amount: z.string().nullable(),
  refunded_amount: z.string(),
  refund_due: z.string().nullable(),
  pending_request: z.object({ type: z.string(), status: z.string() }).nullable(),
  refund_review_required: z.boolean(),
  balance_due: z.string().nullable().optional(),
  delivery: z.string().nullable().optional(),
  return_status: z.string().optional(),
  ordered_quantity: z.number().optional(),
  returned_quantity: z.number().optional(),
  can_fulfill: z.boolean().optional(),
});

export const wireOrderSchema = z.looseObject({
  id: z.number(),
  order_number: z.union([z.string(), z.number()]).transform(String),
  /**
   * Optimistic concurrency token (V2.3). `.catch(0)` and a default so an order
   * read from a backend that predates the column parses as revision 0 — which
   * is exactly what such a backend's rows hold anyway.
   */
  revision: intish.catch(0).default(0),
  last_edited_at: isoDateTime.nullish(),
  customer_id: z.number().nullish(),
  // Transitional parser support for API nodes not migrated in the same deploy.
  contact_id: z.number().nullish(),
  customer_name: z.string().nullish(),
  customer_email: z.string().nullish(),
  customer_phone: z.string().nullish(),
  /** PLAIN STRING — unknown statuses must never fail parsing (see header). */
  status: z.string().min(1),
  payment_method: z.string().nullish(),
  payment: wireOrderPaymentSchema.nullish(),
  status_summary: wireOrderStatusSummarySchema.nullish(),
  shipment: z
    .object({
      carrier: z.string(),
      tracking_number: z.string(),
      tracking_url: z.string().nullable(),
    })
    .nullish(),
  billing_address: z.record(z.string(), z.unknown()).nullish(),
  shipping_address: z.union([z.record(z.string(), z.unknown()), z.array(z.unknown())]).nullish(),
  notes: z.string().nullish(),
  branch_id: z.number().nullish(),
  area_id: z.number().nullish(),
  order_type: z.string(),
  delivery_charges: wireDecimal.nullish(),
  discount_amount: wireDecimal.nullish(),
  discount_meta: z.looseObject({
    discount_type: z.string().nullish(),
    manual: z.looseObject({ discount_amount: wireDecimal.nullish() }).nullish(),
  }).nullish().catch(null),
  sub_total: wireDecimal.nullish(),
  total_amount: wireDecimal,
  tax_amount: wireDecimal.nullish(),
  fbr_status: z.string().nullish(),
  fbr_invoice_number: z.string().nullish(),
  fbr_submitted_at: isoDateTime.nullish(),
  fbr_last_error: z.string().nullish(),
  coupon_code: z.string().nullish(),
  platform: z.string().nullish(),
  sales_channel: z.string().nullish(),
  pos_terminal_id: z.number().nullish(),
  day_session_id: z.number().nullish(),
  created_by_user_id: z.number().nullish(),
  created_at: isoDateTime,
  updated_at: isoDateTime.nullish(),
  /** absent on /orders/new (statusLogs not eager-loaded there). */
  status_logs: z.array(wireOrderStatusLogSchema).nullish(),
  items: z.array(wireOrderItemSchema).nullish(),
  table_id: z.number().nullish(),
  /** Head count on a dine-in tab. A real column upstream since V2.3-02. */
  covers: z.number().nullish(),
  /** create/details/new include `area`; history rows include `terminal`. */
  area: wireOrderAreaSchema.nullish(),
  terminal: wireOrderTerminalSchema.nullish(),
  table: wireOrderTableSchema.nullish(),
  /** Immutable promotion snapshots, present on the detail endpoint. */
  promotions: z.array(wireOrderPromotionSchema).nullish(),
});
export type WireOrder = z.infer<typeof wireOrderSchema>;

/** GET /orders/new — `{ success, data: { pending, inprogress } }`. */
export const wireActiveOrdersSchema = z.looseObject({
  success: z.boolean(),
  data: z.looseObject({
    pending: z.array(wireOrderSchema),
    inprogress: z.array(wireOrderSchema),
  }),
});

/** GET /orders/{id} — `{ success, data }`. */
export const wireOrderDetailsSchema = z.looseObject({
  success: z.boolean(),
  data: wireOrderSchema,
});

/** GET /orders — raw Laravel paginator. */
export const wireOrdersIndexSchema = laravelPaginator(wireOrderSchema);

/** PUT /orders/{id}/status — success/message ONLY; the updated order is NOT returned. */
export const wireStatusUpdateSchema = z.looseObject({
  success: z.boolean(),
  message: z.string().optional(),
});

/** POST /orders/{id}/fbr/retry — the server remains authoritative after retry. */
export const wireFbrRetrySchema = z.looseObject({
  success: z.literal(true),
  fiscal: z.looseObject({
    status: z.string(),
    invoice_number: z.string().nullish(),
    tax_amount: wireDecimal,
    submitted_at: isoDateTime.nullish(),
    error: z.string().nullish(),
  }),
});

// ---------------------------------------------------------------------------
// Normalized internal model (camelCase, numbers for money, ms for age math)
// ---------------------------------------------------------------------------

/** Presentation source derived from `platform`. NEVER implies payment state. */
export type OrderSource = 'web' | 'counter';

export interface OrderItem {
  id: number;
  productId: number;
  /** Root product edit page; differs from productId when the purchased row is a variant. */
  productPageId: number;
  parentItemId: number | null;
  /** Product name from the eager-loaded relation; falls back to "Product #id". */
  name: string;
  sku: string | null;
  variantName: string | null;
  variantLabel: string | null;
  /** Product gallery snapshot returned with the order; first entry is the thumbnail. */
  images: string[];
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  /** Sum of the persisted site-wide and coupon allocations for this line. */
  discountAmount: number;
  /** Named promotion allocations captured when the order was priced. */
  discountAllocations: OrderItemDiscountAllocation[];
  /** Selected options (e.g. { Size: "Large" }); `[]` wire quirk normalized to {}. */
  attributes: Record<string, string>;
  isAddon: boolean;
}

export interface OrderItemDiscountAllocation {
  promotionId: number;
  title: string;
  code: string | null;
  method: string;
  amount: number;
}

export interface OrderPromotionAllocation {
  orderItemId: number;
  amount: number;
}

export interface OrderPromotion {
  id: number;
  promotionId: number | null;
  title: string;
  code: string | null;
  method: string;
  type: string;
  currency: string;
  merchandiseSavings: number;
  shippingSavings: number;
  allocations: OrderPromotionAllocation[];
  state: string;
}

export interface OrderStatusLogEntry {
  id: number;
  status: string;
  notes: string | null;
  createdAt: string;
}

export interface OrderPaymentReceipt {
  available: boolean;
  originalName: string | null;
  mimeType: string | null;
  size: number | null;
  uploadedAt: string | null;
}

export interface OrderPayment {
  checkoutId?: string | null;
  method: string;
  name: string;
  type: string;
  status: string;
  reference: string | null;
  receipt: OrderPaymentReceipt | null;
}

export interface Order {
  statusSummary?: z.infer<typeof wireOrderStatusSummarySchema> | null;
  shipment?: { carrier: string; tracking_number: string; tracking_url: string | null } | null;
  id: number;
  orderNumber: string;
  /** RAW wire status — may be outside POS_ORDER_STATUSES; grouping handles it. */
  status: string;
  /**
   * The customer record this order belongs to, when the wire carries one.
   *
   * Parsed since V2 and, until now, thrown away at this line — which is why
   * the office orders list rendered `customerName` as dead text: the link
   * target for `/customers` was not available in the UI layer at all. Null on
   * a walk-in: a counter sale has a name typed on it and no customer record
   * behind it.
   */
  customerId: number | null;
  customerName: string;
  customerPhone: string | null;
  customerEmail: string | null;
  /** pickup | delivery | dinein — kept open for forward compatibility. */
  orderType: string;
  /** Legacy raw method slug, retained for old order responses. */
  paymentMethod: string | null;
  /** Customer-facing method and safe receipt metadata from the order projection. */
  payment?: OrderPayment | null;
  platform: string | null;
  salesChannel?: string | null;
  source: OrderSource;
  subTotal: number;
  discountAmount: number;
  deliveryCharges: number;
  totalAmount: number;
  /** FBR tax recorded for the fiscal invoice, zero when no tax was returned. */
  taxAmount: number;
  /** Null for orders that were never eligible for FBR submission. */
  fbrStatus: string | null;
  fbrInvoiceNumber: string | null;
  fbrSubmittedAt: string | null;
  fbrLastError: string | null;
  couponCode: string | null;
  shippingAddress: Record<string, string> | null;
  billingAddress?: Record<string, string> | null;
  notes: string | null;
  branchId: number | null;
  areaId: number | null;
  areaName: string | null;
  /** Dine-in only. Null everywhere else — an order is not a tab. */
  tableId: number | null;
  /** The label the floor actually calls that table ("T4"), when loaded. */
  tableLabel: string | null;
  /** Guests on a dine-in tab. Null when never recorded, or not dine-in. */
  covers: number | null;
  posTerminalId: number | null;
  terminalName: string | null;
  daySessionId: number | null;
  /**
   * Optimistic concurrency token (V2.3). The editor sends the revision it
   * LOADED; a stale one comes back 409 with the fresh order instead of silently
   * overwriting whatever the other device saved. 0 on any backend that predates
   * the column, which is what those rows hold.
   */
  revision: number;
  /** When this order was last corrected, or null if it never has been. */
  lastEditedAt: string | null;
  createdAt: string;
  updatedAt: string | null;
  /** Epoch ms of createdAt (0 when unparseable) — for age display only. */
  createdAtMs: number;
  statusLogs: OrderStatusLogEntry[];
  items: OrderItem[];
  promotions: OrderPromotion[];
  /** Recorded manager adjustment, separate from automatic/code savings. */
  manualDiscountAmount?: number;
}

export interface ActiveOrders {
  /** Wire grouping preserved: pending = status 'pending'; inprogress = everything else active. */
  pending: Order[];
  inprogress: Order[];
}

function toAmount(value: string | number | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

function toStringRecord(value: unknown): Record<string, string> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const out: Record<string, string> = {};
  for (const [key, v] of Object.entries(value as Record<string, unknown>)) {
    if (v === null || v === undefined) continue;
    out[key] = typeof v === 'string' ? v : String(v);
  }
  return Object.keys(out).length > 0 ? out : null;
}

function orderItemDiscountAmount(value: unknown): number {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return 0;
  const discounts = (value as Record<string, unknown>).discounts;
  if (!discounts || typeof discounts !== 'object' || Array.isArray(discounts)) return 0;

  return Object.values(discounts as Record<string, unknown>).reduce<number>((total, discount) => {
    if (!discount || typeof discount !== 'object' || Array.isArray(discount)) return total;
    const amount = Number((discount as Record<string, unknown>).amount);
    return Number.isFinite(amount) && amount > 0 ? total + amount : total;
  }, 0);
}

function orderItemDiscountAllocations(value: unknown): OrderItemDiscountAllocation[] {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return [];
  const discounts = (value as Record<string, unknown>).discounts;
  if (!discounts || typeof discounts !== 'object' || Array.isArray(discounts)) return [];
  const promotions = (discounts as Record<string, unknown>).promotions;
  if (!promotions || typeof promotions !== 'object' || Array.isArray(promotions)) return [];
  const allocations = (promotions as Record<string, unknown>).allocations;
  if (!Array.isArray(allocations)) return [];

  return allocations.flatMap((allocation): OrderItemDiscountAllocation[] => {
    if (!allocation || typeof allocation !== 'object' || Array.isArray(allocation)) return [];
    const item = allocation as Record<string, unknown>;
    const promotionId = Number(item.promotion_id);
    const amount = Number(item.amount);
    if (!Number.isFinite(promotionId) || !Number.isFinite(amount) || amount <= 0) return [];
    const code = typeof item.code === 'string' && item.code.trim() ? item.code.trim() : null;

    return [
      {
        promotionId,
        title: typeof item.title === 'string' ? item.title : (code ?? 'Discount'),
        code,
        method: code ? 'code' : 'automatic',
        amount,
      },
    ];
  });
}

export function normalizeOrderItem(wire: WireOrderItem): OrderItem {
  return {
    id: wire.id,
    productId: wire.product_id,
    productPageId: wire.product?.catalog_id ?? wire.product_id,
    parentItemId: wire.parent_order_item_id ?? null,
    name: wire.product?.name ?? `Product #${wire.product_id}`,
    sku: wire.product?.sku ?? null,
    variantName: wire.product?.variant_name?.trim() || null,
    variantLabel: wire.product?.variant_label?.trim() || null,
    images: wire.product?.images ?? [],
    quantity: Number(wire.quantity),
    unitPrice: toAmount(wire.unit_price),
    totalPrice: toAmount(wire.total_price),
    discountAmount: orderItemDiscountAmount(wire.meta),
    discountAllocations: orderItemDiscountAllocations(wire.meta),
    attributes: toStringRecord(wire.attributes) ?? {},
    isAddon: (wire.parent_order_item_id ?? null) !== null,
  };
}

/** Eligibility for the POS walk-in editor; the server enforces permissions and stock rules. */
export function isEditablePosWalkIn(
  order: Order,
  businessType: string | null | undefined,
): boolean {
  return (
    businessType === 'ecommerce' &&
    ['desktop_pos', 'mobile_pos'].includes(order.platform ?? '') &&
    order.orderType === 'pickup' &&
    !['cancelled', 'refunded'].includes(order.status) &&
    (order.statusSummary?.returned_quantity ?? 0) === 0
  );
}

export function normalizeOrder(wire: WireOrder): Order {
  const createdAtMs = Date.parse(wire.created_at);
  return {
    id: wire.id,
    orderNumber: wire.order_number,
    status: wire.status,
    statusSummary: wire.status_summary ?? null,
    shipment: wire.shipment ?? null,
    customerId: wire.customer_id ?? wire.contact_id ?? null,
    customerName: wire.customer_name ?? '',
    customerPhone: wire.customer_phone ?? null,
    customerEmail: wire.customer_email ?? null,
    orderType: wire.order_type,
    paymentMethod: wire.payment_method ?? null,
    payment: wire.payment
      ? {
          checkoutId: wire.payment.checkout_id ?? null,
          method: wire.payment.method,
          name: wire.payment.name,
          type: wire.payment.type,
          status: wire.payment.status,
          reference: wire.payment.reference ?? null,
          receipt: wire.payment.receipt
            ? {
                available: wire.payment.receipt.available,
                originalName: wire.payment.receipt.original_name ?? null,
                mimeType: wire.payment.receipt.mime_type ?? null,
                size: wire.payment.receipt.size ?? null,
                uploadedAt: wire.payment.receipt.uploaded_at ?? null,
              }
            : null,
        }
      : null,
    platform: wire.platform ?? null,
    salesChannel: wire.sales_channel ?? null,
    source: wire.platform === 'web' ? 'web' : 'counter',
    subTotal: toAmount(wire.sub_total),
    discountAmount: toAmount(wire.discount_amount),
    deliveryCharges: toAmount(wire.delivery_charges),
    totalAmount: toAmount(wire.total_amount),
    taxAmount: toAmount(wire.tax_amount),
    fbrStatus: wire.fbr_status ?? null,
    fbrInvoiceNumber: wire.fbr_invoice_number ?? null,
    fbrSubmittedAt: wire.fbr_submitted_at ?? null,
    fbrLastError: wire.fbr_last_error ?? null,
    couponCode: wire.coupon_code ?? null,
    shippingAddress: toStringRecord(wire.shipping_address),
    billingAddress: toStringRecord(wire.billing_address),
    notes: wire.notes ?? null,
    branchId: wire.branch_id ?? null,
    areaId: wire.area_id ?? null,
    areaName: wire.area?.name ?? null,
    tableId: wire.table_id ?? null,
    tableLabel: wire.table?.label ?? null,
    covers: wire.covers ?? null,
    posTerminalId: wire.pos_terminal_id ?? null,
    terminalName: wire.terminal?.name ?? null,
    daySessionId: wire.day_session_id ?? null,
    revision: Number(wire.revision ?? 0),
    lastEditedAt: wire.last_edited_at ?? null,
    createdAt: wire.created_at,
    updatedAt: wire.updated_at ?? null,
    createdAtMs: Number.isFinite(createdAtMs) ? createdAtMs : 0,
    statusLogs: (wire.status_logs ?? []).map((log) => ({
      id: log.id,
      status: log.status,
      notes: log.notes ?? null,
      createdAt: log.created_at,
    })),
    items: (wire.items ?? []).map(normalizeOrderItem),
    promotions: (wire.promotions ?? []).map((promotion) => ({
      id: promotion.id,
      promotionId: promotion.promotion_id ?? null,
      title: promotion.title,
      code: promotion.code ?? null,
      method: promotion.method,
      type: promotion.promotion_type,
      currency: promotion.currency,
      merchandiseSavings: toAmount(promotion.merchandise_savings),
      shippingSavings: toAmount(promotion.shipping_savings),
      allocations: promotion.allocations.map((allocation) => ({
        orderItemId: allocation.order_item_id,
        amount: toAmount(allocation.amount),
      })),
      state: promotion.state,
    })),
    manualDiscountAmount: toAmount(wire.discount_meta?.manual?.discount_amount ??
      (wire.discount_meta?.discount_type === 'pos_manual' ? wire.discount_amount : 0)),
  };
}

// ---------------------------------------------------------------------------
// Item grouping (add-ons under their parent line)
// ---------------------------------------------------------------------------

export interface OrderLine {
  item: OrderItem;
  addons: OrderItem[];
}

/**
 * Groups add-on items (parent_order_item_id) under their parent line.
 * Orphan add-ons whose parent is missing render as top-level lines — an
 * order line must never be dropped from display.
 */
export function groupOrderLines(items: readonly OrderItem[]): OrderLine[] {
  const lines: OrderLine[] = [];
  const byItemId = new Map<number, OrderLine>();
  const orphans: OrderItem[] = [];

  for (const item of items) {
    if (item.parentItemId === null) {
      const line: OrderLine = { item, addons: [] };
      lines.push(line);
      byItemId.set(item.id, line);
    }
  }
  for (const item of items) {
    if (item.parentItemId === null) continue;
    const parent = byItemId.get(item.parentItemId);
    if (parent) parent.addons.push(item);
    else orphans.push(item);
  }
  for (const orphan of orphans) lines.push({ item: orphan, addons: [] });
  return lines;
}
